// File: src/pages/ReferralPartnerDashboardPage.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { ReactComponent as TribeLogo } from '../assets/TribeLogo.svg';
import MinimalFooter from '../components/MinimalFooter';
import CreatorAgreementGate from '../components/CreatorAgreementGate';
import EventHelpTooltip from '../components/EventPreview/EventHelpTooltip';
import { IoArrowBack } from 'react-icons/io5';
import { LuLogOut } from 'react-icons/lu';

import {
  LuArrowLeft, LuCheck, LuChevronDown, LuCopy, LuRefreshCw, LuSearch,
} from 'react-icons/lu';

const API = process.env.REACT_APP_API_URL || '';
// Preview only. Set to false to restore the authenticated, live dashboard.
const USE_DEMO_DATA = false;

export function buildDemoData(today = new Date()) {
  const currentMonth = monthKey(today);
  const [year, month] = currentMonth.split('-').map(Number);
  const day = Number(new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, day: 'numeric' }).format(today));
  const previousMonth = monthKey(new Date(Date.UTC(year, month - 2, 15, 20)));
  const names = [
    ['Maya', 'Chen'], ['Jordan', 'Rivera'], ['Sofia', 'Patel'], ['Alex', 'Kim'],
    ['Camila', 'Reyes'], ['Noah', 'Brooks'], ['Amara', 'Johnson'], ['Ethan', 'Park'],
    ['Isabella', 'Garcia'], ['Lucas', 'Bennett'], ['Aaliyah', 'Davis'], ['Leo', 'Nguyen'],
    ['Chloe', 'Martin'], ['Mateo', 'Lopez'], ['Priya', 'Shah'], ['Julian', 'Wilson'],
    ['Zoe', 'Taylor'], ['Owen', 'Clark'], ['Layla', 'Hassan'], ['Adrian', 'Flores'],
    ['Nina', 'Singh'], ['Miles', 'Anderson'], ['Elena', 'Torres'], ['Kai', 'Wong'],
    ['Ruby', 'Thompson'], ['Daniel', 'Ortiz'], ['Aria', 'Robinson'], ['Isaac', 'Lee'],
    ['Naomi', 'Lewis'], ['Sebastian', 'Cruz'], ['Stella', 'Young'], ['Andre', 'Thomas'],
    ['Leila', 'Ahmed'], ['Theo', 'Walker'], ['Valentina', 'Morales'], ['Jules', 'Reed'],
    ['Jasmine', 'Scott'], ['Gabriel', 'Perez'], ['Avery', 'Collins'], ['Sam', 'Mitchell'],
  ];
  const makeReferral = (name, index, period) => {
    const [periodYear, periodMonth] = period.split('-').map(Number);
    const availableDays = Math.min(day, new Date(Date.UTC(periodYear, periodMonth, 0)).getUTCDate());
    const date = `${period}-${String(availableDays - index % availableDays).padStart(2, '0')}T20:00:00Z`;
    const application = index % 5 === 0;
    return {
      id: `demo-${period}-${index}`, personId: `demo-person-${period}-${index}`,
      firstName: name[0], lastName: name[1], creditedAt: date,
      ...(application ? { kind: 'application', applicationSubmittedAt: date } : { accountCreatedAt: date }),
      reservation: index % 3 !== 0 ? { eventName: ['Dinner & Karaoke', 'Wine Tasting & Pizza', 'Beach Yoga & Picnic'][index % 3], status: 'reserved' } : null,
    };
  };
  return {
    partner: { id: 'demo-creator', referralCode: 'DEMO40', joinedAt: `${previousMonth}-01T12:00:00-07:00`, status: 'active' },
    referrals: [
      ...names.map((name, index) => makeReferral(name, index, currentMonth)),
      ...[['Lily','Foster'],['Chris','Ramirez'],['Hannah','Bell'],['Dante','Price'],['Sienna','Evans'],['Max','Turner'],['Anika','Mehta'],['Ben','Hughes'],['Cleo','Ward'],['Rafael','Silva'],['Iris','Bailey'],['Dylan','Cooper']].map((name, index) => makeReferral(name, index, previousMonth)),
    ],
  };
}

export default function ReferralPartnerDashboardPage() {
  const navigate = useNavigate();
  const auth = useContext(AuthContext) || {};
  const userId = auth.currentUser?._id || auth.currentUser?.id || localStorage.getItem('userId');
  const userKey = String(userId || '');
  const currentUserKeyRef = useRef(userKey);
  currentUserKeyRef.current = userKey;
  const [data, setData] = useState(null);
  const [dataOwner, setDataOwner] = useState(null);
  const [gateRevision, setGateRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const controllerRef = useRef(null);
  const requestRef = useRef(0);
  const mountedRef = useRef(false);
  const refreshRef = useRef(null);

  async function loadDashboard({ throwOnError = false, restartAgreement = false } = {}) {
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const requestId = ++requestRef.current;
    setRefreshing(true);
    setError('');
    try {
      if (USE_DEMO_DATA) {
        if (mountedRef.current && requestRef.current === requestId && currentUserKeyRef.current === userKey) {
          setDataOwner(userKey);
          setData(buildDemoData());
        }
        return;
      }
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setData(null);
        setError('Please sign in to view your creator dashboard.');
        if (throwOnError) {
          const authError = new Error('Please sign in to view your creator dashboard.');
          authError.statusCode = 401;
          throw authError;
        }
        return;
      }
      const response = await axios.get(`${API}/referral-partners/me`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      });
      if (!mountedRef.current || requestRef.current !== requestId || currentUserKeyRef.current !== userKey) return;
      const payload = response.data;
      // A gated response deliberately has no referrals, earnings or invite code.
      // Handle it before validating the normal dashboard response.
      if (payload?.requiresAgreement === true) {
        const completeAgreement = ['version', 'contentHash', 'title', 'text'].every(key =>
          typeof payload.agreement?.[key] === 'string' && payload.agreement[key].trim().length > 0
        );
        if (!completeAgreement) {
          setData(null);
          throw new Error('The creator agreement could not be loaded.');
        }
        setDataOwner(userKey);
        setData({ requiresAgreement: true, agreement: payload.agreement, agreementStatus: payload.agreementStatus });
        if (restartAgreement) setGateRevision(revision => revision + 1);
        return payload;
      }
      // Only the authenticated server can confirm acceptance and unlock the dashboard.
      if (payload?.requiresAgreement !== false || payload.agreementStatus?.accepted !== true
          || !payload.partner || !Array.isArray(payload.referrals)) {
        setData(null);
        throw new Error('Unexpected dashboard response');
      }
      setDataOwner(userKey);
      setData(payload);
      return payload;
    } catch (err) {
      if (controller.signal.aborted || !mountedRef.current || requestRef.current !== requestId || currentUserKeyRef.current !== userKey) return;
      const authStatus = err?.response?.status || err?.statusCode;
      if ([401, 403].includes(authStatus)) {
        setData(null);
        setError(authStatus === 401 ? 'Please sign in to view your creator dashboard.' : 'This account does not have access to the creator dashboard.');
      } else {
        setError('We couldn’t load your creator dashboard. Please try again.');
      }
      // The signing component must be told when its follow-up refresh fails.
      if (throwOnError) throw err;
    } finally {
      if (mountedRef.current && requestRef.current === requestId && currentUserKeyRef.current === userKey) { setLoading(false); setRefreshing(false); }
    }
  }
  refreshRef.current = loadDashboard;

  useEffect(() => {
    mountedRef.current = true;
    setData(null);
    setLoading(true);
    refreshRef.current();
    const onFocus = () => { refreshRef.current(); };
    window.addEventListener('focus', onFocus);
    return () => { mountedRef.current = false; controllerRef.current?.abort(); window.removeEventListener('focus', onFocus); };
  }, [userKey]);

  async function handlePortalAction() {
    if (auth.currentUser?.isHost === true) { navigate('/portal'); return; }
    if (typeof auth.logout === 'function') await auth.logout();
    ['accessToken', 'authToken', 'token', 'userId'].forEach(key => localStorage.removeItem(key));
    navigate('/events', { replace: true });
  }

  async function handleDesktopLogout() {
    try {
      if (typeof auth.logout === 'function') await auth.logout();
    } finally {
      ['accessToken', 'authToken', 'token', 'refreshToken', 'sessionId', 'userId']
        .forEach(key => localStorage.removeItem(key));
      window.location.replace('/events');
    }
  }

  const visibleData = dataOwner === userKey ? data : null;

  if (visibleData?.requiresAgreement === true) {
    return <CreatorAgreementGate
      key={`${userKey}:${gateRevision}`}
      agreement={visibleData.agreement}
      onAccepted={() => loadDashboard({ throwOnError: true, restartAgreement: true })}
      onReloadAgreement={() => loadDashboard({ throwOnError: true, restartAgreement: true })}
      onBack={() => navigate('/events')}
    />;
  }

  return <ReferralDashboard
    demo={USE_DEMO_DATA}
    header={<><style>{HEADER_RESPONSIVE_STYLES}</style><PartnerDashboardHeader
      onBack={() => navigate(-1)}
      onDesktopBack={() => navigate('/portal')}
      onLogout={handleDesktopLogout}
      currentUser={auth.currentUser}
    /></>}
    footer={<MinimalFooter />}
    partner={visibleData?.partner || {}}
    referrals={visibleData?.referrals || []}
    monthlySummaries={visibleData?.monthlySummaries || []}
    loading={loading || (data !== null && dataOwner !== userKey)}
    refreshing={refreshing}
    error={error}
    onRefresh={loadDashboard}
    onBack={() => navigate(-1)}
    onPortalAction={handlePortalAction}
    portalActionLabel={auth.currentUser?.isHost === true ? 'Switch portal' : 'Log out'}
    logo={<TribeLogo aria-label="Tribe" />}
  />;
}


export const REFERRAL_RATE = 3;
export const BONUS_TIERS = [
  { referrals: 25, bonus: 100 },
  { referrals: 50, bonus: 250 },
  { referrals: 100, bonus: 600 },
  { referrals: 200, bonus: 1200 },
];
const TIME_ZONE = 'America/Los_Angeles';
const CONTACT_EMAIL = 'nati@jointribe.io';
const IDEAS = [
  {
    title: 'Use cold DMs to reach new people',
    text: 'Send personal invitations on Instagram or TikTok, or start conversations on Bumble For Friends. Explain what Tribe is, why it might interest them, and offer a private invite.',
  },
  {
    title: 'Throw a party with a Tribe membership guest list',
    text: 'Host a party, dinner, or meetup where guests sign up for Tribe to attend. Talk to the team first so we can agree on the plan and make sure your referrals are credited.',
  },
  {
    title: 'Introduce Tribe to a community',
    text: 'Connect with a student organization, alumni group, hobby club, or local group chat. Ask the organizer about sharing an invitation or bringing the group to Tribe together.',
  },
  {
    title: 'Pitch a local collaboration',
    text: 'Know a café, gym, studio, or shop with a community of its own? Bring us an idea for a meetup or membership invitation that would work for their audience.',
  },
];

function validDate(value) {
  if (!value) return null;
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = new Date(dateOnly ? `${value}T12:00:00Z` : value);
  if (Number.isNaN(date.getTime()) || (dateOnly && date.toISOString().slice(0, 10) !== value)) return null;
  return date;
}

export function monthKey(value) {
  if (typeof value === 'string' && /^\d{4}-\d{2}(?:-\d{2})?$/.test(value)) {
    const year = Number(value.slice(0, 4));
    const month = Number(value.slice(5, 7));
    return year >= 1970 && month >= 1 && month <= 12 && (value.length === 7 || validDate(value)) ? value.slice(0, 7) : '';
  }
  const date = validDate(value);
  if (!date) return '';
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: TIME_ZONE, year: 'numeric', month: '2-digit' }).formatToParts(date);
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}`;
}

function earliestDate(values) {
  return values.map(validDate).filter(Boolean).sort((a, b) => a - b)[0]?.toISOString() || '';
}

function stringId(value) {
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  return value?._id ? String(value._id) : '';
}

function aliasesFor(row) {
  const keys = [];
  for (const field of ['personId', 'userId', 'applicantId']) {
    const value = stringId(row[field]);
    if (value) keys.push(`${field === 'applicantId' ? 'applicant' : 'person'}:${value}`);
  }
  const email = String(row.email || '').trim().toLowerCase();
  const phone = String(row.phoneNumber || row.phone || '').replace(/\D/g, '');
  if (email) keys.push(`email:${email}`);
  if (phone) keys.push(`phone:${phone}`);
  const id = stringId(row._id || row.id);
  if (id) keys.push(`record:${id}`);
  return keys;
}

function isBooked(row) {
  const reservation = row.firstEventReservation || row.reservation;
  if (['cancelled', 'canceled'].includes(String(row.status || '').toLowerCase()) && !row.attendedAt && !row.firstEventAttendedAt) return false;
  if (reservation && ['cancelled', 'canceled'].includes(String(reservation.status || '').toLowerCase())) return false;
  return Boolean(row.firstEventBooked === true || row.firstEventBookedAt || row.firstEventReservedAt || row.attendedAt || row.firstEventAttendedAt || reservation || row.status === 'event_reserved');
}

// Defensive display deduplication. The backend must supply a stable personId and
// immutable creditedAt across application -> account creation for durable credit.
export function normalizeReferrals(input = []) {
  const groups = new Set();
  const byAlias = new Map();
  for (const row of Array.isArray(input) ? input : []) {
    if (!row || typeof row !== 'object' || row.isDuplicate === true || row.isSelfReferral === true) continue;
    const aliases = aliasesFor(row);
    const matches = [...new Set(aliases.map(key => byAlias.get(key)).filter(Boolean))];
    const group = matches[0] || { rows: [], aliases: new Set() };
    if (!matches.length) groups.add(group);
    for (const other of matches.slice(1)) {
      group.rows.push(...other.rows);
      other.aliases.forEach(alias => { group.aliases.add(alias); byAlias.set(alias, group); });
      groups.delete(other);
    }
    group.rows.push(row);
    aliases.forEach(alias => { group.aliases.add(alias); byAlias.set(alias, group); });
  }
  return [...groups].map((group, index) => {
    const merged = {};
    group.rows.forEach(row => Object.entries(row).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') merged[key] = value;
    }));
    const creditedAt = earliestDate(group.rows.map(row => row.firstCreditedAt || row.creditedAt || earliestDate([
      row.accountCreatedAt, row.applicationSubmittedAt, row.appliedAt, row.createdAt,
    ])));
    const accountDate = earliestDate(group.rows.map(row => row.accountCreatedAt));
    const applicationDate = earliestDate(group.rows.map(row => row.applicationSubmittedAt || row.appliedAt || ((row.kind === 'application' || row.type === 'application') ? row.createdAt : '')));
    const hasAccount = Boolean(accountDate);
    const applied = group.rows.some(row => row.applicationSubmittedAt || row.appliedAt || row.kind === 'application' || row.type === 'application');
    return {
      ...merged,
      displayId: [...group.aliases][0] || `referral-${index}`,
      displayName: merged.name || [merged.firstName, merged.lastName || (merged.lastInitial ? `${merged.lastInitial}.` : '')].filter(Boolean).join(' ') || 'Tribe member',
      profilePicUrl: merged.profilePicUrl || merged.profilePhotoUrl || merged.avatarUrl || '',
      creditedAt,
      sourceLabel: applied && (!hasAccount || (applicationDate && applicationDate <= accountDate)) ? 'Applied' : 'Account created',
      hasBookedFirstEvent: isBooked(merged),
    };
  }).sort((a, b) => (validDate(b.creditedAt)?.getTime() || 0) - (validDate(a.creditedAt)?.getTime() || 0));
}

export function bonusForCount(count) {
  const safeCount = Math.max(0, Math.floor(Number(count) || 0));
  const reached = BONUS_TIERS.filter(tier => safeCount >= tier.referrals).slice(-1)[0] || null;
  const next = BONUS_TIERS.find(tier => safeCount < tier.referrals) || null;
  return { reached, next, bonus: reached?.bonus || 0, remaining: next ? next.referrals - safeCount : 0 };
}

// Each bonus is a checkpoint. Interpolate within the current interval so the
// journey stays readable even though the signup gaps between tiers differ.
export function bonusRoadmapPosition(count) {
  const value = Math.max(0, Number(count) || 0);
  const checkpoints = [{ referrals: 0, position: 0 }, ...BONUS_TIERS.map((tier, index) => ({ referrals: tier.referrals, position: 12.5 + index * 25 }))];
  for (let i = 1; i < checkpoints.length; i += 1) {
    const before = checkpoints[i - 1];
    const after = checkpoints[i];
    if (value <= after.referrals) return before.position + (value - before.referrals) / (after.referrals - before.referrals) * (after.position - before.position);
  }
  return 87.5;
}

export function buildMonthOptions(partner, rows, today = new Date()) {
  const current = monthKey(today);
  const keys = [monthKey(partner?.joinedAt), ...rows.map(row => monthKey(row.creditedAt))].filter(key => key && key <= current);
  const first = keys.sort()[0] || current;
  const result = [];
  for (let cursor = current; cursor >= first;) {
    result.push(cursor);
    let [year, month] = cursor.split('-').map(Number);
    month -= 1;
    if (month === 0) { year -= 1; month = 12; }
    cursor = `${year}-${String(month).padStart(2, '0')}`;
  }
  return result;
}

function money(value) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}
function monthLabel(key) {
  const [year, month] = key.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(Date.UTC(year, month - 1, 15)));
}
function dateLabel(value) {
  const date = validDate(value);
  return date ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: TIME_ZONE }).format(date) : '—';
}

function Avatar({ referral }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => { setFailed(false); }, [referral.profilePicUrl]);
  const initials = referral.displayName.split(/\s+/).slice(0, 2).map(name => name[0] || '').join('').toUpperCase();
  return referral.profilePicUrl && !failed ? (
    <img className="cr-avatar" src={referral.profilePicUrl} alt="" width="38" height="38" loading="lazy" onError={() => setFailed(true)} />
  ) : <span className="cr-avatar cr-avatar-fallback" aria-hidden="true">{initials}</span>;
}

export function ReferralDashboard({
  partner = {}, referrals = [], monthlySummaries = [], loading = false, refreshing = false, error = '',
  onRefresh, onBack, onPortalAction, portalActionLabel = '', logo = null, header = null, footer = null,
  today, demo = false,
}) {
  const [tab, setTab] = useState('referrals');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [search, setSearch] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const tabRefs = useRef([]);
  const copyTimer = useRef(null);
  const now = today || new Date();
  const rows = useMemo(() => normalizeReferrals(referrals), [referrals]);
  const options = useMemo(() => buildMonthOptions(partner, rows, now), [partner.joinedAt, rows, monthKey(now)]);
  const period = options.includes(selectedMonth) ? selectedMonth : options[0];
  const monthRows = useMemo(() => rows.filter(row => monthKey(row.creditedAt) === period), [rows, period]);
  const serverMonth = monthlySummaries.find(month => month.period === period);
  const isHistoricalPolicy = Boolean(serverMonth && serverMonth.programVersion !== 'signup-v2');
  const count = serverMonth ? Number(serverMonth.referralCount) : monthRows.length;
  const bonus = serverMonth ? {
    bonus: Number(serverMonth.bonusAmount || 0),
    reached: serverMonth.bonusTier ? { referrals: serverMonth.bonusTier, bonus: serverMonth.bonusAmount } : null,
    next: serverMonth.nextBonusTier ? { referrals: serverMonth.nextBonusTier, bonus: serverMonth.nextBonusAmount } : null,
    remaining: Number(serverMonth.referralsToNextTier || 0),
  } : bonusForCount(count);
  const commission = serverMonth ? Number(serverMonth.baseEarnings || 0) : count * REFERRAL_RATE;
  const filteredRows = monthRows.filter(row => row.displayName.toLowerCase().includes(search.trim().toLowerCase()));
  const code = String(partner.referralCode || '');
  const teamUrl = `mailto:${CONTACT_EMAIL}`;
  const missingDates = rows.some(row => !row.creditedAt);
  useEffect(() => () => { if (copyTimer.current) clearTimeout(copyTimer.current); }, []);

  async function copy(value, successMessage) {
    if (!value) return;
    clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(value);
      setCopyMessage(successMessage);
    } catch {
      setCopyMessage('Couldn’t copy. You can select and copy your code manually.');
    }
    copyTimer.current = setTimeout(() => setCopyMessage(''), 2500);
  }

  function changeTab(event, index) {
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : ['ArrowLeft', 'ArrowRight'].includes(event.key) ? 1 - index : null;
    if (next === null) return;
    event.preventDefault();
    setTab(next === 0 ? 'referrals' : 'ideas');
    tabRefs.current[next]?.focus();
  }

  return (
    <div className="cr-page">
      <style>{STYLES}</style>
      <div className="cr-shell">
        {header || <header className="cr-header">
          <div className="cr-header-left">{onBack ? <button className="cr-text-button" type="button" onClick={onBack}><LuArrowLeft size={16} aria-hidden="true" />Back</button> : null}</div>
          <div className="cr-logo">{logo || <span>tribe</span>}</div>
          <div className="cr-header-right">{onPortalAction && portalActionLabel ? <button className="cr-text-button" type="button" onClick={onPortalAction}>{portalActionLabel}</button> : null}</div>
        </header>}

        <main>
          <div className="cr-nav-row">
          <div className="cr-tabs" role="tablist" aria-label="Creator dashboard">
            {[
              { id: 'referrals', label: 'My referrals' },
              { id: 'ideas', label: 'Did you know?' },
            ].map((item, index) => (
              <button key={item.id} ref={node => { tabRefs.current[index] = node; }} type="button" role="tab" id={`cr-tab-${item.id}`} aria-controls={`cr-panel-${item.id}`} aria-selected={tab === item.id} tabIndex={tab === item.id ? 0 : -1} onClick={() => setTab(item.id)} onKeyDown={event => changeTab(event, index)}>{item.label}</button>
            ))}
          </div>
            <section className="cr-invite" aria-labelledby="cr-code-title">
              <span id="cr-code-title" className="cr-invite-label">Invite code</span>
              <div className="cr-code-chip"><span className="cr-code" aria-label={code ? `Invite code ${code}` : 'Invite code unavailable'}>{loading ? 'Loading…' : code || 'Unavailable'}</span><button className="cr-copy-code" type="button" aria-label="Copy code" title="Copy invite code" disabled={loading || !code} onClick={() => copy(code, 'Invite code copied.')}><LuCopy size={14} aria-hidden="true" /><span className="cr-sr-only">Copy code</span></button></div>
              <div className="cr-copy-status" role="status">{copyMessage}</div>
            </section>
          </div>

          <section id="cr-panel-referrals" role="tabpanel" aria-labelledby="cr-tab-referrals" hidden={tab !== 'referrals'} tabIndex={0} className="cr-panel">
            {error ? <div role="alert" className="cr-error">{error}{onRefresh ? <button type="button" className="cr-text-button" onClick={onRefresh} disabled={refreshing}>Try again</button> : null}</div> : null}

            <div className="cr-data" aria-busy={loading} hidden={Boolean(error && !code && !rows.length && !loading)}>
              <section className="cr-monthly" aria-label="Monthly referral results">
                <div className="cr-month-heading"><h1>Your referrals{demo ? <span className="cr-demo-badge">Sample data</span> : null}</h1>
              <div className="cr-period-controls">
                <label className="cr-month-label"><span className="cr-sr-only">Referral month</span><select aria-label="Referral month" value={period} disabled={loading} onChange={event => { setSelectedMonth(event.target.value); setSearch(''); }}>{options.map(key => <option key={key} value={key}>{monthLabel(key)}</option>)}</select></label>
                {onRefresh ? <button type="button" className="cr-icon-button" aria-label="Refresh referrals" disabled={loading || refreshing} onClick={onRefresh}><LuRefreshCw size={16} aria-hidden="true" /></button> : null}
              </div>
                </div>
                <div className="cr-progress-message">
                  <p className="cr-current-count">{loading ? 'Loading your progress…' : <>You’re at <strong>{count.toLocaleString('en-US')} {count === 1 ? 'referral' : 'referrals'}</strong> {period === monthKey(now) ? 'this month' : `in ${monthLabel(period)}`}.</>}</p>
                  {!loading ? <p className="cr-next-goal">{isHistoricalPolicy ? 'Historical earnings use the previous program terms.' : bonus.next ? <><strong>{bonus.remaining} more {bonus.remaining === 1 ? 'referral' : 'referrals'}</strong> for a <strong>{money(bonus.next.bonus)} bonus</strong>.</> : <>You’ve reached the highest bonus: <strong>{money(bonus.bonus)}</strong>.</>}</p> : null}
                </div>
                <div className="cr-summary">
                  <div><h2>Referrals</h2><strong className="cr-number">{loading ? '—' : count.toLocaleString('en-US')}</strong></div>
                  <div><h2>Referral earnings</h2><strong className="cr-number">{loading ? '—' : money(commission + bonus.bonus)}</strong><p>{loading ? 'Loading…' : <>{money(commission)} commission + {money(bonus.bonus)} bonus</>}</p></div>
                </div>
                <details className="cr-bonus">
                  <summary id="cr-bonus-title"><span>Monthly bonus details</span><LuChevronDown size={16} aria-hidden="true" /></summary>
                  {isHistoricalPolicy ? <p className="cr-small-note">This month’s referral earnings and bonus are preserved from the previous program.</p> : <div className="cr-roadmap" aria-busy={loading}>
                    <div className="cr-roadmap-track" aria-hidden="true" />
                    <div className="cr-roadmap-fill" style={{ width: `${loading ? 0 : bonusRoadmapPosition(count)}%` }} aria-hidden="true" />
                    <div className="cr-roadmap-position" role="progressbar" aria-label="Monthly bonus progress" aria-valuemin={0} aria-valuemax={200} aria-valuenow={loading ? 0 : Math.min(count, 200)} aria-valuetext={loading ? 'Loading' : `${count} referrals. ${money(bonus.bonus)} bonus unlocked.${bonus.next ? ` ${bonus.remaining} more referrals to the ${money(bonus.next.bonus)} bonus.` : ' Highest bonus reached.'}`} style={{ left: `${loading ? 0 : bonusRoadmapPosition(count)}%` }} hidden={loading} />
                    <ol className="cr-roadmap-stops">{BONUS_TIERS.map(tier => {
                      const reached = !loading && count >= tier.referrals;
                      const current = !loading && bonus.reached?.referrals === tier.referrals;
                      const next = !loading && bonus.next?.referrals === tier.referrals;
                      return <li key={tier.referrals} className={`cr-roadmap-stop${reached ? ' is-reached' : ''}${next ? ' is-next' : ''}`} aria-current={current ? 'step' : undefined}>
                        <span className="cr-roadmap-amount">{money(tier.bonus)}<span className="cr-sr-only"> bonus</span></span>
                        <span className="cr-roadmap-dot" aria-hidden="true">{reached ? <LuCheck size={10} /> : null}</span>
                        <span className="cr-roadmap-threshold">{tier.referrals}<span className="cr-roadmap-unit"> referrals</span></span>
                        <span className="cr-sr-only">{loading ? '' : reached ? 'Unlocked' : 'Not yet unlocked'}</span>
                      </li>;
                    })}</ol>
                  </div>}
                  {!isHistoricalPolicy ? <p className="cr-roadmap-note">$3 per referral + your highest monthly bonus.</p> : null}
                </details>
              </section>

              <section className="cr-list-section" aria-labelledby="cr-list-title">
                <div className="cr-list-heading"><h2 id="cr-list-title">Referred members</h2></div>
                {monthRows.length > 5 || search ? <label className="cr-search"><LuSearch size={15} aria-hidden="true" /><input type="search" value={search} placeholder="Search referrals" aria-label="Search referrals" onChange={event => setSearch(event.target.value)} /></label> : null}
                {loading ? <p className="cr-empty" role="status">Loading your referrals…</p> : filteredRows.length ? (
                  <table className="cr-table">
                    <caption className="cr-sr-only">Your referrals for {monthLabel(period)}</caption>
                    <thead><tr><th scope="col">Member</th><th scope="col">Joined / applied</th><th scope="col">First event</th></tr></thead>
                    <tbody>{filteredRows.map(referral => <tr key={referral.displayId}>
                      <th scope="row"><div className="cr-member"><Avatar referral={referral} /><span>{referral.displayName}</span></div></th>
                      <td><time dateTime={referral.creditedAt}>{dateLabel(referral.creditedAt)}</time><span className="cr-row-note">{referral.sourceLabel}</span></td>
                      <td>{referral.hasBookedFirstEvent ? <span className="cr-booked"><LuCheck size={13} aria-hidden="true" />Booked</span> : <span className="cr-not-booked">Not yet</span>}</td>
                    </tr>)}</tbody>
                  </table>
                ) : <div className="cr-empty">{search.trim() && monthRows.length ? 'No referrals match your search.' : <><strong>No referrals yet this month.</strong><p>Share your private invite code to get started.</p></>}</div>}
                {missingDates && !loading ? <p className="cr-small-note">Some referral dates are missing. <a href={teamUrl}>Email {CONTACT_EMAIL}</a> to correct your records.</p> : null}
              </section>
            </div>
          </section>

          <section id="cr-panel-ideas" role="tabpanel" aria-labelledby="cr-tab-ideas" hidden={tab !== 'ideas'} tabIndex={0} className="cr-panel cr-ideas-panel">
            <h1>More ways to bring people to Tribe</h1>
            <p className="cr-ideas-intro">Referrals from any of these count toward your commissions and bonuses.</p>
            <div className="cr-idea-list">{IDEAS.map(({ title, text }) => <article className="cr-idea" key={title}><h2>{title}</h2><p>{text}</p></article>)}</div>
            <div className="cr-contact"><h2>Have an idea, or want to try one of these?</h2><p>Talk to the Tribe team: <a href={teamUrl}>{CONTACT_EMAIL}</a></p></div>
          </section>
        </main>
        {footer || <footer className="cr-footer">Tribe Creator Program</footer>}
      </div>
    </div>
  );
}

const STYLES = `
.cr-page { min-height:100vh; padding:24px 32px; background:#fff; color:#171717; font:14px/1.5 Inter,ui-sans-serif,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif; -webkit-font-smoothing:antialiased; }
.cr-page,.cr-page *, .cr-page *::before,.cr-page *::after { box-sizing:border-box; }
.cr-page [hidden] { display:none !important; }.cr-shell { max-width:820px; margin:auto; }
.cr-page button,.cr-page input,.cr-page select { font:inherit; }.cr-page button,.cr-page select { cursor:pointer; }.cr-page button:disabled,.cr-page select:disabled { opacity:.45; cursor:default; }
.cr-page :is(button,a,input,select,summary):focus-visible { outline:2px solid #171717; outline-offset:4px; }
.cr-page a { color:inherit; text-underline-offset:3px; }
.cr-page h1 { margin:0; font-size:25px; line-height:1.25; font-weight:600; letter-spacing:-.035em; }
.cr-page h2 { margin:0; font-size:14px; font-weight:600; }.cr-page p { margin:0; }
.cr-header { display:grid; grid-template-columns:1fr auto 1fr; align-items:center; min-height:64px; margin-bottom:20px; }.cr-header-right { justify-self:end; }.cr-logo { font-size:28px; font-weight:700; }.cr-logo svg { display:block; width:72px; height:auto; }
.cr-nav-row { display:flex; align-items:center; justify-content:space-between; gap:20px; border-bottom:1px solid #e9e9e9; }.cr-tabs { display:flex; flex-shrink:0; gap:28px; }.cr-tabs button { position:relative; min-height:46px; padding:0 0 12px; border:0; color:#777; background:none; font-size:13px; }.cr-tabs button[aria-selected="true"] { color:#171717; }.cr-tabs button[aria-selected="true"]::after { content:''; position:absolute; bottom:-1px; left:0; right:0; height:2px; background:#171717; }
.cr-panel { padding-top:28px; }.cr-text-button { display:inline-flex; align-items:center; justify-content:center; gap:6px; border:0; padding:6px 0; color:#666; background:none; font-size:12px !important; }.cr-text-button:hover { color:#111; }
.cr-list-heading { margin-bottom:10px; }.cr-list-heading > h2 { font-size:15px; color:#222; }
.cr-invite { position:relative; display:flex; align-items:center; justify-content:flex-end; gap:10px; padding-bottom:10px; min-width:0; }.cr-invite-label { color:#777; font-size:11px; white-space:nowrap; }.cr-code-chip { display:flex; align-items:center; gap:10px; max-width:240px; min-width:0; padding:5px 7px 5px 11px; border:1px solid #e3e3de; border-radius:7px; background:#f7f7f3; }.cr-code { min-width:0; overflow-wrap:anywhere; font:600 13px/1.4 ui-monospace,SFMono-Regular,Menlo,monospace; user-select:all; color:#222; }.cr-copy-code { display:grid; place-items:center; width:26px; height:26px; flex-shrink:0; padding:0; border:0; color:#555; background:none; border-radius:4px; }.cr-copy-code:hover { background:#e9e9e4; }.cr-copy-status { position:absolute; top:100%; right:0; z-index:2; color:#555; font-size:10px; white-space:nowrap; }

.cr-demo-badge { display:inline-block; vertical-align:middle; margin-left:12px; padding:4px 8px; border-radius:4px; background:#f1f1ed; color:#888; font-size:10px; line-height:1.2; font-weight:400; letter-spacing:0; }
.cr-monthly { padding:0; }.cr-month-heading { display:flex; align-items:center; justify-content:space-between; gap:16px; }.cr-period-controls { display:flex; align-items:center; gap:5px; }.cr-month-label select { max-width:180px; min-height:36px; padding:4px 6px; border:0; background:#fff; color:#666; font-size:12px; }.cr-icon-button { display:grid; place-items:center; width:30px; height:32px; padding:0; border:0; background:none; color:#777; }
.cr-progress-message { margin-top:24px; }.cr-current-count { color:#777; font-size:13px; }.cr-current-count strong { color:#444; font-weight:550; }.cr-next-goal { margin-top:5px !important; color:#666; font-size:19px; line-height:1.4; letter-spacing:-.02em; }.cr-next-goal strong { color:#222; font-weight:600; }
.cr-summary { display:grid; grid-template-columns:1fr 1fr; gap:32px; max-width:600px; padding:28px 0 0; }.cr-summary > div + div { padding-left:32px; border-left:1px solid #e6e6e2; }.cr-summary h2 { color:#555; font-size:12px; font-weight:500; }.cr-number { display:block; margin-top:9px; font-size:42px; line-height:1.15; font-weight:600; letter-spacing:-.045em; }.cr-summary p { margin-top:7px; color:#777; font-size:12px; }
.cr-bonus { margin-top:22px; padding:0 24px; border:1px solid #ecece6; border-radius:12px; background:#f7f7f3; color:#666; font-size:12px; }.cr-bonus > summary { display:flex; align-items:center; justify-content:space-between; gap:16px; min-height:54px; cursor:pointer; list-style:none; color:#333; font-size:13px; font-weight:550; }.cr-bonus > summary::-webkit-details-marker { display:none; }.cr-bonus[open] { padding-bottom:20px; }.cr-bonus[open] > summary svg { transform:rotate(180deg); }.cr-roadmap-heading { display:flex; justify-content:space-between; align-items:baseline; gap:14px; }.cr-roadmap-heading h2 { font-size:15px; font-weight:600; color:#222; }.cr-roadmap-heading p { font-size:12px; }.cr-roadmap-heading strong { color:#333; font-weight:500; }
.cr-roadmap { position:relative; margin-top:24px; }.cr-roadmap-track,.cr-roadmap-fill { position:absolute; top:35px; left:0; height:2px; background:#e9e9e9; }.cr-roadmap-track { right:12.5%; background:#d9d9d1; }.cr-roadmap-fill { background:#333; max-width:87.5%; }.cr-roadmap-stops { position:relative; z-index:1; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); list-style:none; margin:0; padding:0; }.cr-roadmap-stop { display:flex; flex-direction:column; align-items:center; color:#72726c; }.cr-roadmap-amount { font-size:21px; line-height:24px; font-weight:550; letter-spacing:-.025em; }.cr-roadmap-dot { display:grid; place-items:center; width:14px; height:14px; margin:5px 0 9px; border:2px solid #cfcfc6; border-radius:50%; background:#f7f7f3; color:white; }.cr-roadmap-threshold { font-size:11px; line-height:17px; }.cr-roadmap-stop.is-next { color:#222; }.cr-roadmap-stop.is-next .cr-roadmap-amount { font-weight:650; }.cr-roadmap-stop.is-next .cr-roadmap-dot { border-color:#444; }.cr-roadmap-stop.is-reached { color:#222; }.cr-roadmap-stop.is-reached .cr-roadmap-dot { background:#333; border-color:#333; }.cr-roadmap-stop[aria-current="step"] .cr-roadmap-dot { outline:3px solid #e9e9e9; outline-offset:2px; }
.cr-roadmap-position { position:absolute; top:32px; width:8px; height:8px; margin-left:-4px; border-radius:50%; background:#333; box-shadow:0 0 0 3px #f7f7f3; }.cr-roadmap-note { margin-top:18px !important; font-size:11px; color:#686862; }

.cr-list-section { margin-top:36px; padding-top:25px; border-top:1px solid #e3e3df; }.cr-search { display:flex; align-items:center; gap:8px; max-width:250px; min-height:36px; margin:15px 0 5px; color:#999; }.cr-search input { width:100%; min-width:0; border:0; padding:6px 0; background:none; color:#333; font-size:12px; }
.cr-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:13px; }.cr-table th,.cr-table td { padding:14px 8px; border-bottom:1px solid #eee; text-align:left; vertical-align:middle; }.cr-table th:first-child,.cr-table td:first-child { width:46%; padding-left:0; }.cr-table th:last-child,.cr-table td:last-child { width:22%; padding-right:0; text-align:right; }.cr-table thead th { padding-top:14px; padding-bottom:12px; color:#666; font-size:11px; font-weight:400; }.cr-table tbody th { font-weight:500; }.cr-member { display:flex; align-items:center; gap:10px; min-width:0; }.cr-member > span { overflow-wrap:anywhere; }.cr-avatar { display:block; width:32px; height:32px; flex:0 0 32px; border-radius:50%; object-fit:cover; background:#f0f0ee; }.cr-avatar-fallback { display:grid; place-items:center; color:#777; font-size:10px; }.cr-row-note { display:block; margin-top:2px; color:#999; font-size:10px; }.cr-booked { display:inline-flex; align-items:center; gap:4px; font-size:11px; }.cr-not-booked { color:#999; font-size:11px; }
.cr-empty { padding:35px 24px; text-align:center; color:#777; font-size:13px; }.cr-empty strong { display:block; color:#333; font-weight:550; }.cr-empty p { margin-top:5px; font-size:12px; }.cr-small-note { margin-top:14px !important; color:#888; font-size:11px; }.cr-error { display:flex; align-items:center; justify-content:space-between; gap:16px; margin-bottom:24px; color:#963f36; font-size:12px; }
.cr-ideas-panel { max-width:600px; }.cr-ideas-intro { margin-top:12px !important; color:#777; font-size:13px; }.cr-idea-list { display:grid; gap:25px; margin-top:30px; }.cr-idea h2 { margin-bottom:6px; }.cr-idea p,.cr-contact p { color:#777; font-size:13px; line-height:1.65; }.cr-contact { margin-top:32px; padding-top:24px; border-top:1px solid #eee; }.cr-contact p { margin-top:7px; }.cr-footer { margin-top:48px; color:#999; font-size:11px; }
.cr-sr-only { position:absolute; width:1px; height:1px; padding:0; margin:-1px; overflow:hidden; clip:rect(0,0,0,0); white-space:nowrap; border:0; }
@media(max-width:640px) { .cr-page { padding:16px 20px; }.cr-panel { padding-top:23px; }.cr-nav-row { gap:10px; }.cr-tabs { gap:16px; }.cr-tabs button { font-size:12px; }.cr-invite { gap:6px; }.cr-invite-label { position:absolute; width:1px; height:1px; overflow:hidden; clip:rect(0,0,0,0); }.cr-code-chip { max-width:145px; padding:4px 4px 4px 8px; gap:5px; }.cr-code { font-size:12px; }.cr-roadmap-heading { align-items:flex-start; flex-direction:column; gap:5px; }.cr-roadmap-amount { font-size:18px; }.cr-roadmap-threshold { font-size:10px; }.cr-month-heading h1 { font-size:21px; }.cr-month-label select { max-width:145px; font-size:11px; }.cr-summary { gap:20px; padding-top:24px; }.cr-summary > div + div { padding-left:20px; }.cr-number { font-size:36px; }.cr-bonus { margin-top:20px; padding:0 16px; }.cr-bonus[open] { padding-bottom:18px; }.cr-next-goal { font-size:18px; }.cr-list-section { margin-top:28px; padding-top:20px; }.cr-table { font-size:12px; }.cr-table th,.cr-table td { padding:13px 5px; }.cr-avatar { width:28px; height:28px; flex-basis:28px; }.cr-member { gap:8px; }.cr-summary p { font-size:11px; }.cr-ideas-panel h1 { font-size:23px; } }
@media(max-width:380px) { .cr-tabs { gap:12px; }.cr-tabs button { font-size:11px; }.cr-code-chip { max-width:112px; }.cr-nav-row { gap:8px; }.cr-invite { gap:3px; } .cr-page { padding-right:16px; padding-left:16px; }.cr-summary { gap:28px; }.cr-month-heading { gap:8px; }.cr-month-label select { max-width:132px; }.cr-month-heading h1 { font-size:19px; } }
`;

function PartnerDashboardHeader({
  onBack,
  onDesktopBack,
  onLogout,
  currentUser,
}) {
  const profileName = [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ')
    || currentUser?.name || currentUser?.email || 'Creator';
  const avatarUrl = currentUser?.profilePicUrl || '/default-avatar.png';

  return (
    <div
      className="partner-main-header"
      style={styles.partnerMainHeader}
    >
      {/* Desktop: centered logo above the navigation row */}
      <div className="partner-header-desktop-logo-row" style={styles.desktopLogoRow}>
        <TribeLogo style={styles.partnerHeaderLogoDesktop} aria-label="Tribe" />
      </div>
      <div
        className="partner-header-desktop-row"
        style={styles.desktopHeaderRow}
      >
        <div style={styles.desktopHeaderBrand}>
          <button
            type="button"
            style={styles.desktopHeaderBack}
            onClick={onDesktopBack}
            aria-label="Back to portal selection"
          >
            <LuArrowLeft size={18} strokeWidth={1.8} aria-hidden="true" />
            <span>Back</span>
          </button>
        </div>

        <div style={styles.desktopHeaderTitle}>REFERRAL DASHBOARD</div>

        <div style={styles.desktopHeaderRight}>
          <img
            src={avatarUrl}
            alt={`${profileName}'s profile`}
            title={profileName}
            style={styles.desktopHeaderAvatar}
            onError={event => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = '/default-avatar.png';
            }}
          />
          <button
            type="button"
            style={styles.desktopHeaderAction}
            onClick={onLogout}
          >
            <LuLogOut size={15} aria-hidden="true" />
            Log out
          </button>
        </div>
      </div>

      {/* Mobile: keep Tribe above the nav row */}
      <div
        className="partner-header-mobile-logo-row"
        style={styles.partnerLogoRow}
      >
        <TribeLogo
          style={styles.partnerHeaderLogo}
          aria-label="Tribe"
        />
      </div>

      <div
        className="partner-header-mobile-nav-row"
        style={styles.header}
      >
        <div className="partner-header-mobile-left">
          <button
            type="button"
            style={styles.mobileHeaderBackButton}
            onClick={onBack}
            aria-label="Go back"
          >
            <IoArrowBack />
          </button>
        </div>

        <div style={styles.headerTitle}>
          REFERRAL DASHBOARD
        </div>

        <div className="partner-header-mobile-right">
          <EventHelpTooltip
            title="Referral dashboard"
            body="Copy your private invite code, track new signups and applications, and see your monthly referral earnings and bonuses."
          />
        </div>
      </div>
    </div>
  );
}





const styles = {
  partnerMainHeader: {
    width: '100%',
    background: '#fff',
  },
  desktopLogoRow: {
    height: 48,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  desktopHeaderRow: {
    height: 54,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
    alignItems: 'center',
    width: '100%',
    position: 'relative',
  },
  desktopHeaderBrand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 16,
    minWidth: 0,
  },
  desktopHeaderBack: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    minHeight: 40,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: '#666',
    fontFamily: 'inherit',
    fontSize: 12,
    cursor: 'pointer',
    flexShrink: 0,
  },
  desktopHeaderAvatar: {
    width: 28,
    height: 28,
    flexShrink: 0,
    borderRadius: '50%',
    border: '1px solid #eee',
    objectFit: 'cover',
  },
  partnerHeaderLogoDesktop: {
    width: 72,
    height: 'auto',
    display: 'block',
  },
  desktopHeaderTitle: {
    justifySelf: 'center',
    color: '#000',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: '.02em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
  desktopHeaderRight: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 14,
    minWidth: 0,
  },
  desktopHeaderAction: {
    minHeight: 40,
    padding: 0,
    border: 0,
    background: 'transparent',
    color: '#666',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  partnerLogoRow: {
    height: 48,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  partnerHeaderLogo: {
    width: 72,
    height: 'auto',
    display: 'block',
  },
  header: {
    height: 54,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  mobileHeaderBackButton: {
    width: 40,
    height: 28,
    border: 0,
    borderRadius: 999,
    background: '#0b0b0b',
    color: '#fff',
    padding: 0,
    display: 'grid',
    placeItems: 'center',
    fontSize: 17,
    cursor: 'pointer',
  },
  headerTitle: {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    fontSize: 15,
    fontWeight: 900,
    lineHeight: 1,
    letterSpacing: '.02em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  },
};

const HEADER_RESPONSIVE_STYLES = `
.partner-main-header { margin-bottom: 22px; }
.partner-header-mobile-logo-row, .partner-header-mobile-nav-row { display: none !important; }
.partner-header-mobile-left, .partner-header-mobile-right { position: absolute; top: 50%; transform: translateY(-50%); display: flex; align-items: center; }
.partner-header-mobile-left { left: 0; }
.partner-header-mobile-right { right: 0; z-index: 102; }
@media (max-width: 760px) {
  .partner-header-desktop-row, .partner-header-desktop-logo-row { display: none !important; }
  .partner-header-mobile-logo-row, .partner-header-mobile-nav-row { display: flex !important; }
  .partner-header-mobile-logo-row { padding-top: 8px; }
  .partner-header-mobile-nav-row { min-height: 48px; position: relative; z-index: 101; overflow: visible; }
}
`;
